import TextReveal from "@/components/motion/TextReveal";
import GridBackground from "@/components/primitives/GridBackground";
import BlueprintBackdrop from "@/components/motion/BlueprintBackdrop";

export default function ProblemSection() {
    return (
        <section
            id="problem"
            data-testid="problem-section"
            className="relative bg-tca-panel overflow-hidden"
        >
            {/* Ambient blueprint imprint — beneath the grid, beneath everything */}
            <BlueprintBackdrop
                src="/brand/blueprint-01.jpg"
                opacity={0.085}
                parallaxRange={140}
                rotateRange={1.4}
            />
            {/* Faint cyan grid layered above blueprint — unifies the visual language */}
            <div className="absolute inset-0 pointer-events-none opacity-50">
                <GridBackground vignette={false} />
            </div>
            {/* Soft cyan glow accents */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full"
                style={{
                    background: "radial-gradient(circle, rgba(0,224,232,0.10), transparent 70%)",
                    filter: "blur(40px)",
                }}
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-48 -right-40 w-[600px] h-[600px] rounded-full"
                style={{
                    background: "radial-gradient(circle, rgba(255,107,255,0.06), transparent 70%)",
                    filter: "blur(48px)",
                }}
            />

            <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 py-28 sm:py-40">
                <div className="grid grid-cols-12 gap-8">
                    <div className="col-span-12 lg:col-span-2 lg:pt-3">
                        <div
                            className="font-label text-tca-cyan/85"
                            data-testid="problem-label"
                        >
                            01 · The Problem
                        </div>
                    </div>

                    <div className="col-span-12 lg:col-span-9 lg:col-start-3">
                        <TextReveal
                            as="h2"
                            data-testid="problem-headline"
                            className="font-display text-[40px] leading-[1.06] sm:text-[60px] sm:leading-[1.02] lg:text-[80px] lg:leading-[1.0] tracking-[-0.025em] text-tca-ink"
                            stagger={0.06}
                            duration={0.85}
                        >
                            Everyone is building from the middle.
                        </TextReveal>

                        <div className="mt-10 sm:mt-12 max-w-2xl space-y-6 text-tca-ink-muted text-base sm:text-lg leading-relaxed">
                            <TextReveal
                                as="p"
                                data-testid="problem-body-1"
                                stagger={0.018}
                                duration={0.55}
                            >
                                Workflows fractured across nine tools. Prompts pasted into
                                chat windows like sticky notes. Agents wired together with
                                duct-tape and optimism. The output is chaos dressed as
                                progress.
                            </TextReveal>

                            <TextReveal
                                as="p"
                                data-testid="problem-body-2"
                                stagger={0.018}
                                duration={0.55}
                            >
                                The market has confused <span className="text-tca-ink">activity</span>{" "}
                                for architecture. Every day, builders ship more without the
                                discipline to know what they're shipping <em className="text-tca-ink not-italic">on top of</em>.
                            </TextReveal>

                            <TextReveal
                                as="p"
                                data-testid="problem-body-3"
                                stagger={0.018}
                                duration={0.55}
                                className="border-l border-tca-cyan/40 pl-5"
                            >
                                <span className="text-tca-ink">The Creator's Agent</span>{" "}
                                is the structure that was missing. Letter by letter — A
                                through H, deployable today — a methodical curriculum for
                                engineering intelligent systems the right way. No
                                shortcuts. No templates. No middle.
                            </TextReveal>
                        </div>

                        {/* Footer marker row */}
                        <div className="mt-14 sm:mt-16 flex items-center gap-4">
                            <div className="h-px flex-1 bg-tca-ink-dim/40" />
                            <span
                                className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-ink-muted"
                                data-testid="problem-marker"
                            >
                                A → H · ACTIVE
                            </span>
                            <div className="h-px w-12 bg-tca-cyan/60" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
